import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { api, setCsrfToken, type Me, type Permission } from './api'

/**
 * Who is signed in, for the whole SPA.
 *
 * ⚠️ **This is convenience, not access control.** Every protected route carries a
 * server-side dependency; hiding a nav item only stops a user from clicking
 * something that would 403 anyway. `docs/APPLICATION_SPEC.md` §2.3.
 */
interface SessionState {
  me: Me | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  can: (permission: Permission) => boolean
}

const SessionContext = createContext<SessionState | null>(null)

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  const adopt = useCallback((next: Me | null) => {
    setMe(next)
    setCsrfToken(next?.csrf_token ?? null)
  }, [])

  const refresh = useCallback(async () => {
    try {
      const next = await api.me()
      // 200 with authenticated:false is the signed-out case, not an error.
      adopt(next.authenticated ? next : null)
    } catch {
      adopt(null)
    } finally {
      setLoading(false)
    }
  }, [adopt])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signIn = useCallback(
    async (email: string, password: string) => {
      adopt(await api.login(email, password))
    },
    [adopt],
  )

  const signOut = useCallback(async () => {
    try {
      await api.logout()
    } finally {
      adopt(null)
    }
  }, [adopt])

  const can = useCallback(
    (permission: Permission) => {
      if (!me) return false
      // Mirrors the server's SessionUser.has_grant: an Admin implies every
      // grant, and revenue is Admin-only (RV-3) whatever `grants` says.
      if (me.role === 'admin') return true
      if (permission === 'revenue_view' || permission === 'revenue_enter') return false
      return (me.grants as Permission[]).includes(permission)
    },
    [me],
  )

  const value = useMemo(
    () => ({ me, loading, signIn, signOut, refresh, can }),
    [me, loading, signIn, signOut, refresh, can],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionState {
  const context = useContext(SessionContext)
  if (!context) throw new Error('useSession Must Be Used Inside a SessionProvider')
  return context
}
